import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { api } from "../../api";
import { getEnvironmentConfig } from "../../utils/environment";
import ConciergeLogo from "../../assets/images/ccw_logo_white.png";
import {
  AU_SITE_P1_SUBSCRIPTIONS,
  P1_SIGNUP_BASE_URL,
  SITE_CATALOG_LOCK_IN_MONTHS,
  CCW_P1_SUBSCRIPTIONS_UTM,
  CCW_SUBSCRIPTIONS_QR_PARTNER_ID,
  CCW_SUBSCRIPTIONS_QR_PARTNER_USER_ID,
} from "../../config/p1SiteSubscriptions.au";
import "../Subscriptions/Subscriptions.css";

const VEHICLE_TYPE_MAP = {
  Sedan: "sedan",
  "Sedan/Hatch": "sedan",
  Hatch: "sedan",
  Wagon: "wagon",
  SUV: "wagon",
  "Wagon/SUV": "wagon",
  "Wagon / SUV": "wagon",
  "SUV/Wagon": "wagon",
  "4WD": "4wd",
  "4WD/7Seater": "4wd",
  "4WD/7 Seater": "4wd",
  "4WD / 7 Seater": "4wd",
  "7Seater": "4wd",
  "7 Seater": "4wd",
};

const normalizeVehicleType = (vehicleType) => {
  if (!vehicleType) return null;
  return VEHICLE_TYPE_MAP[vehicleType] || vehicleType.toLowerCase();
};

const SubscriptionsV2 = () => {
  const envCfg = getEnvironmentConfig();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Monthly toggle hidden for now — only 12-month lock-in is offered.
  // Set to true to re-enable the Monthly option in the pricing toggle.
  const SHOW_MONTHLY_TOGGLE = false;
  const [pricingToggle, setPricingToggle] = useState("locked");
  const [bookingData, setBookingData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [cartData, setCartData] = useState(null);
  const [bookingLocation, setBookingLocation] = useState(null);
  const [resolvedSiteId, setResolvedSiteId] = useState(null);
  const [locationName, setLocationName] = useState(null);
  const pageViewTrackedRef = useRef(false);

  const returnUrl = searchParams.get("returnUrl");
  const bookingId = searchParams.get("booking");
  const siteIdFromUrl = searchParams.get("siteId");
  const useSiteCatalog = Boolean(siteIdFromUrl);

  const trackP1Event = useCallback(
    async (eventType, extra = {}) => {
      const siteId =
        extra.siteId ??
        resolvedSiteId ??
        siteIdFromUrl ??
        bookingData?.idLocation?.posId ??
        bookingLocation?.posId;
      if (!siteId) return;

      try {
        await api("booking-flow/p1-subscription/track", "POST", {
          eventType,
          siteId,
          bookingId: bookingId || null,
          ...extra,
        });
      } catch (err) {
        console.error("Error tracking P1 subscription event:", err);
      }
    },
    [resolvedSiteId, siteIdFromUrl, bookingData, bookingLocation, bookingId]
  );

  useEffect(() => {
    if (!siteIdFromUrl) return;

    const resolveLocationFromUrl = async () => {
      try {
        const response = await api(
          `booking-flow/location-by-site/${siteIdFromUrl}`,
          "GET"
        );
        if (response.status === 200 && response.data.success) {
          const { posId, seoName, name } = response.data.location;
          setResolvedSiteId(posId);
          setLocationName(seoName || name);
        }
      } catch (err) {
        console.error("Error resolving location from siteId:", err);
      }
    };

    resolveLocationFromUrl();
  }, [siteIdFromUrl]);

  useEffect(() => {
    if (siteIdFromUrl || !bookingData) return;

    const posId = bookingData.idLocation?.posId || bookingLocation?.posId;
    const seoName =
      bookingData.idLocation?.seoName ||
      bookingLocation?.seoName ||
      cartData?.step1Data?.selectedLocation?.seoName;
    if (posId) {
      setResolvedSiteId(posId);
      if (seoName) setLocationName(seoName);
    }
  }, [siteIdFromUrl, bookingData, bookingLocation, cartData]);

  useEffect(() => {
    if (!resolvedSiteId || pageViewTrackedRef.current) return;
    pageViewTrackedRef.current = true;
    trackP1Event("page_view", { siteId: resolvedSiteId });
  }, [resolvedSiteId, trackP1Event]);

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSiteCatalog]);

  useEffect(() => {
    if (bookingId) {
      fetchBookingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const fetchBookingData = async () => {
    try {
      const response = await api(`booking-flow/booking/${bookingId}`, "GET");
      if (response.status === 200 && response.data.success) {
        setBookingData(response.data.booking);
        setCartData(response.data.cart);
        setBookingLocation(response.data.location || null);
        setCustomerData(
          response.data.cart?.step3Data || response.data.cart?.step1Data
        );
      }
    } catch (error) {
      console.error("Error fetching booking data:", error);
    }
  };

  const fetchSubscriptions = async () => {
    if (useSiteCatalog) {
      setSubscriptions(AU_SITE_P1_SUBSCRIPTIONS);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api("subscription/get-all", "GET");

      if (response.status === 200 && response.data.success) {
        setSubscriptions(response.data.subscriptions);
      } else {
        setError("Failed to load subscriptions");
      }
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Error loading subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToExtras = () => {
    if (returnUrl) {
      if (returnUrl.includes("/booking-extras")) {
        window.location.href = returnUrl;
      } else {
        navigate("/");
      }
    } else if (bookingId) {
      navigate(`/manage-booking/${bookingId}`);
    } else {
      navigate("/");
    }
  };

  const getResolvedVehicleType = () => {
    const raw =
      cartData?.step1Data?.vehicleType ||
      customerData?.vehicleType ||
      bookingData?.vehicleType;
    return normalizeVehicleType(raw);
  };

  const appendCommonSignupParams = (params) => {
    const effectiveSiteId =
      resolvedSiteId ||
      siteIdFromUrl ||
      bookingData?.idLocation?.posId ||
      bookingLocation?.posId;
    const effectiveLocationName =
      locationName ||
      bookingData?.idLocation?.seoName ||
      bookingLocation?.seoName ||
      cartData?.step1Data?.selectedLocation?.seoName ||
      bookingData?.idLocation?.name ||
      bookingLocation?.name;

    const fallbackName = bookingData?.posResponse?.BookingName || "";
    const [fallbackFirst, ...restName] = fallbackName.split(" ");
    const fallbackLast = restName.join(" ") || "";

    const email =
      customerData?.email || bookingData?.posResponse?.BookingEmail || "";
    const phone =
      customerData?.phone || bookingData?.posResponse?.BookingPhone || "";
    const firstName = customerData?.firstName || fallbackFirst || "";
    const lastName = customerData?.lastName || fallbackLast || "";

    if (email) params.append("email", email);
    if (phone) params.append("phone", phone);
    if (firstName) params.append("firstName", firstName);
    if (lastName) params.append("lastName", lastName);
    if (bookingId) params.append("booking", bookingId);

    if (effectiveSiteId) {
      params.append("siteId", String(effectiveSiteId));
      if (effectiveLocationName) {
        params.append("locationName", effectiveLocationName);
      }
    }

    const webhookUrl = `${envCfg.api.apiUrl}booking-flow/subscription-webhook`;
    params.append("webhookUrl", webhookUrl);
  };

  const appendCcwUtmParams = (params, card) => {
    const utmKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ];

    for (const key of utmKeys) {
      const fromPage = searchParams.get(key);
      if (fromPage) {
        params.set(key, fromPage);
        continue;
      }
      if (CCW_P1_SUBSCRIPTIONS_UTM[key]) {
        params.set(key, CCW_P1_SUBSCRIPTIONS_UTM[key]);
      }
    }

    if (!params.get("utm_content") && card?.id) {
      params.set("utm_content", card.id);
    }
  };

  const generateSiteCatalogUrl = (card) => {
    const params = new URLSearchParams();
    appendCommonSignupParams(params);
    appendCcwUtmParams(params, card);

    // Locked partner links (CCW Subscriptions QR) — pricing/lock-in live on the link
    if (card.signupUrl) {
      const url = new URL(card.signupUrl);
      params.forEach((value, key) => {
        url.searchParams.set(key, value);
      });
      return url.toString();
    }

    // Open productId flow (e.g. Sparkle variant pricing)
    let baseUrl = `${P1_SIGNUP_BASE_URL}${card.p1ProductId}`;
    let monthlyPrice;
    let vehicleType = getResolvedVehicleType();

    if (card.pricingModel === "variant") {
      const tier = vehicleType || "sedan";
      monthlyPrice = card.vehiclePricing[tier] ?? card.vehiclePricing.sedan;
      if (vehicleType) params.append("vehicleType", vehicleType);
    } else {
      monthlyPrice = card.monthlyPrice;
      params.append("vehicleType", card.vehicleType);
    }

    params.append("rrp", String(monthlyPrice * 12));
    params.append("lockInPeriodMonths", String(SITE_CATALOG_LOCK_IN_MONTHS));
    params.append(
      "partnerId",
      card.partnerId || CCW_SUBSCRIPTIONS_QR_PARTNER_ID
    );
    params.append(
      "partnerUserId",
      card.partnerUserId || CCW_SUBSCRIPTIONS_QR_PARTNER_USER_ID
    );

    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${params.toString()}`;
  };

  const generateSubscriptionUrl = (subscription) => {
    let baseUrl = subscription.externalLink;

    if (!baseUrl) {
      console.warn(
        `No externalLink found for subscription: ${subscription.name}`
      );
      return "#";
    }

    if (pricingToggle === "locked") {
      if (
        subscription.stripeLockedId &&
        subscription.stripeLockedId.trim() !== ""
      ) {
        baseUrl = `${baseUrl}${subscription.stripeLockedId}`;
      } else {
        return "#";
      }
    } else {
      if (subscription.stripeId && subscription.stripeId.trim() !== "") {
        baseUrl = `${baseUrl}${subscription.stripeId}`;
      } else {
        return "#";
      }
    }

    const params = new URLSearchParams();

    if (customerData || bookingId || bookingData) {
      appendCommonSignupParams(params);

      const vehicleType = getResolvedVehicleType();
      if (vehicleType) params.append("vehicleType", vehicleType);

      const fullYearRRP =
        pricingToggle === "locked"
          ? subscription.lockedPrice * 12
          : subscription.monthlyPrice * 12;
      params.append("rrp", fullYearRRP.toString());
    }

    if (params.toString()) {
      const separator = baseUrl.includes("?") ? "&" : "?";
      baseUrl = `${baseUrl}${separator}${params.toString()}`;
    }

    return baseUrl;
  };

  const trackSignUpClick = (subscriptionName) => {
    const effectiveSiteId =
      resolvedSiteId ||
      siteIdFromUrl ||
      bookingData?.idLocation?.posId ||
      bookingLocation?.posId;

    if (!effectiveSiteId) return;

    trackP1Event("sign_up_click", {
      siteId: effectiveSiteId,
      subscriptionName,
    }).catch(() => {});
  };

  const handleSignUpLinkClick = (event, subscriptionOrCard, signupUrl) => {
    if (!signupUrl || signupUrl === "#") {
      event.preventDefault();
      return;
    }
    const subscriptionName = useSiteCatalog
      ? subscriptionOrCard.displayName
      : subscriptionOrCard.name;
    trackSignUpClick(subscriptionName);
  };

  const getPrice = (subscription) => {
    return pricingToggle === "locked"
      ? subscription.lockedPrice
      : subscription.monthlyPrice;
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const sortedSubscriptions = [...subscriptions].sort((a, b) => {
    const priceA = pricingToggle === "locked" ? a.lockedPrice : a.monthlyPrice;
    const priceB = pricingToggle === "locked" ? b.lockedPrice : b.monthlyPrice;
    return priceA - priceB;
  });

  const renderSiteCatalogPrice = (card) => {
    if (card.pricingModel === "variant") {
      return (
        <ul className="p1-tier-pricing">
          <li>Sedan / Hatch — {formatPrice(card.vehiclePricing.sedan)}/mo</li>
          <li>Wagon / SUV — {formatPrice(card.vehiclePricing.wagon)}/mo</li>
          <li>4WD / 7 Seater — {formatPrice(card.vehiclePricing["4wd"])}/mo</li>
        </ul>
      );
    }

    return (
      <div className="p1-subscription-price">
        <span className="p1-price-amount">{formatPrice(card.monthlyPrice)}</span>
        <span className="p1-price-period">/month</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="subscriptions-container">
        <Helmet>
          <title>Unlimited Wash Club | Concierge Car Wash</title>
          <meta name="robots" content="index,follow" />
          <meta
            name="description"
            content="Choose the perfect unlimited car wash subscription plan."
          />
          <link
            rel="canonical"
            href={`${envCfg.bookingUrl}p1-subscriptions`}
          />
        </Helmet>
        <div className="subscriptions-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading subscription plans...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscriptions-container">
        <Helmet>
          <title>Unlimited Wash Club | Concierge Car Wash</title>
          <meta name="robots" content="index,follow" />
          <meta
            name="description"
            content="Choose the perfect unlimited car wash subscription plan."
          />
          <link
            rel="canonical"
            href={`${envCfg.bookingUrl}p1-subscriptions`}
          />
        </Helmet>
        <div className="subscriptions-content">
          <div className="error-message">
            <h2>❌ Error</h2>
            <p>{error}</p>
            <button onClick={handleBackToExtras} className="retry-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscriptions-container">
      <Helmet>
        <title>Unlimited Wash Club | Concierge Car Wash</title>
        <meta name="robots" content="index,follow" />
        <meta
          name="description"
          content="Choose the perfect unlimited car wash subscription plan."
        />
        <link
          rel="canonical"
          href={`${envCfg.bookingUrl}p1-subscriptions`}
        />
      </Helmet>
      <div
        className={`subscriptions-content${
          useSiteCatalog ? " site-catalog-layout" : ""
        }`}
      >
        <div className="subscriptions-header">
          <img src={ConciergeLogo} alt="Concierge Car Wash" className="logo" />
          <h1>Unlimited Wash Club</h1>
          <p className="subtitle">Choose the perfect plan for your car care needs</p>
          {locationName && (
            <p className="subtitle location-context">
              Signing up from {locationName}
            </p>
          )}

          <div className="powered-by-section">
            <p className="powered-by-text">
              Subscriptions powered by{" "}
              <a
                href="https://priorityonewashclub.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="priority-one-link"
              >
                Priority One Wash Club
              </a>
            </p>
          </div>

          {useSiteCatalog && (
            <div className="pricing-toggle pricing-toggle-single">
              <button type="button" className="toggle-btn active" aria-current="true">
                6-Month Lock-In
              </button>
            </div>
          )}

          {!useSiteCatalog && (
            <div
              className={`pricing-toggle${
                !SHOW_MONTHLY_TOGGLE ? " pricing-toggle-single" : ""
              }`}
            >
              {SHOW_MONTHLY_TOGGLE && (
                <button
                  className={`toggle-btn ${
                    pricingToggle === "monthly" ? "active" : ""
                  }`}
                  onClick={() => setPricingToggle("monthly")}
                >
                  Monthly
                </button>
              )}
              <button
                className={`toggle-btn ${
                  pricingToggle === "locked" ? "active" : ""
                }`}
                onClick={() => setPricingToggle("locked")}
              >
                12-Month Lock-In
              </button>
            </div>
          )}
        </div>

        <div
          className={`subscriptions-grid${
            useSiteCatalog ? " site-catalog-grid" : ""
          }`}
        >
          {useSiteCatalog
            ? subscriptions.map((card) => (
                <div
                  key={card.id}
                  className={`p1-subscription-card ${
                    card.popular ? "popular" : ""
                  }`}
                >
                  <div className="p1-subscription-header">
                    <h3 className="p1-subscription-name">{card.displayName}</h3>
                    {card.vehicleLabel && (
                      <p className="p1-vehicle-label">{card.vehicleLabel}</p>
                    )}
                    {renderSiteCatalogPrice(card)}
                  </div>

                  <div className="p1-subscription-features">
                    <h4>What's Included:</h4>
                    <ul>
                      {card.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p1-subscription-actions">
                    {(() => {
                      const signupUrl = generateSiteCatalogUrl(card);
                      return (
                        <a
                          href={signupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p1-signup-btn"
                          onClick={(event) =>
                            handleSignUpLinkClick(event, card, signupUrl)
                          }
                        >
                          Sign Up
                        </a>
                      );
                    })()}
                  </div>

                  {card.popular && (
                    <div className="p1-popular-badge">Most Popular</div>
                  )}
                </div>
              ))
            : sortedSubscriptions.map((subscription) => (
                <div
                  key={subscription._id}
                  className={`p1-subscription-card ${
                    subscription.popular ? "popular" : ""
                  }`}
                >
                  <div className="p1-subscription-header">
                    <h3 className="p1-subscription-name">{subscription.name}</h3>
                    <div className="p1-subscription-price">
                      <span className="p1-price-amount">
                        {formatPrice(getPrice(subscription))}
                      </span>
                      <span className="p1-price-period">
                        {pricingToggle === "locked"
                          ? "/month (12-month)"
                          : "/month"}
                      </span>
                    </div>
                  </div>

                  <div className="p1-subscription-features">
                    <h4>What's Included:</h4>
                    <ul>
                      {subscription.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p1-subscription-actions">
                    {(() => {
                      const signupUrl = generateSubscriptionUrl(subscription);
                      return (
                        <a
                          href={signupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p1-signup-btn"
                          onClick={(event) =>
                            handleSignUpLinkClick(
                              event,
                              subscription,
                              signupUrl
                            )
                          }
                        >
                          Sign Up
                        </a>
                      );
                    })()}
                  </div>

                  {subscription.popular && (
                    <div className="p1-popular-badge">Most Popular</div>
                  )}
                </div>
              ))}
        </div>

        <div className="back-section">
          <button className="back-btn" onClick={handleBackToExtras}>
            {returnUrl && returnUrl.includes("/booking-extras")
              ? "← Back to Extras"
              : bookingId
              ? "← Manage Booking"
              : "← Back to Home"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsV2;
